import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import baseApi from "../Http/baseApi";
import { logout, postLoginAsync } from "./authSlice";
import { toast } from "react-toastify";

function normalizePatientsResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      data: payload,
      total: payload.length,
    };
  }

  return {
    data: payload?.data || [],
    total: payload?.total ?? payload?.data?.length ?? 0,
  };
}

export const getAdminStatsAsync = createAsyncThunk(
  "patients/getAdminStatsAsync",
  async (_, thunkAPI) => {
    try {
      const response = await baseApi.get("admin/stats");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Unable to load dashboard stats.";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getPatientsAsync = createAsyncThunk(
  "patients/getPatientsAsync",
  async (payload, thunkAPI) => {
    try {
      const params = new URLSearchParams({
        user_type: payload.userType,
        user_id: payload.userId,
      });

      if (payload.page) {
        params.append("page", String(payload.page));
      }

      if (payload.limit) {
        params.append("limit", String(payload.limit));
      }

      if (payload.bpDetail) {
        params.append("bp_detail", payload.bpDetail);
      }

      const response = await baseApi.get(`patients?${params.toString()}`);
      return normalizePatientsResponse(response.data);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Unable to load patients.";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getPatientsDraftsAsync = createAsyncThunk(
  "patients/getPatientsDraftsAsync",
  async (payload) => {
    const response = await baseApi.get(
      `drafts?user_type=` + payload.userType + `&user_id=` + payload.userId
    );
    return response.data;
  }
);
export const getAllCasesAsync = createAsyncThunk(
  "patients/getAllCasesAsync",
  async (payload) => {
    const response = await baseApi.get(`allcases`);
    return response.data;
  }
);
export const getPatientsByIdAsync = createAsyncThunk(
  "patients/getPatientsByIdAsync",
  async (payload) => {
    const response = await baseApi.get(`patients/${payload.id}`);
    return response.data;
  }
);

export const getFamilyMembersAsync = createAsyncThunk(
  "patients/getFamilyMembersAsync",
  async (payload, thunkAPI) => {
    try {
      const response = await baseApi.get(
        `family-members?user_id=` + payload.userId
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Unable to load family members.";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const friendlyError = (err, fallback) => {
  if (!err.response) {
    return "Unable to reach the server. Please check your internet connection and try again.";
  }
  return err.response.data?.message || fallback;
};

export const addPatientAsync = createAsyncThunk(
  "patients/addPatientsAsync",
  async (payload) => {
    let postData = { ...payload.basicInfo };
    postData.draft = payload.clickStatus;
    try {
      const response = await baseApi.post("patients", postData);
      toast.success("Data Saved Successful!");
      return response.data;
    } catch (err) {
      toast.error(friendlyError(err, "Unable to save this case. Please try again."));
      throw err;
    }
  }
);

export const addFamilyAsync = createAsyncThunk(
  "patients/addFamilyAsync",
  async (payload) => {
    // console.log(payload);
    let postData = { ...payload.basicInfo };
    postData.draft = payload.clickStatus; 
    try {
      const response = await baseApi.post("add_family_member", postData);
      toast.success("Data Saved Successful!");
    } catch (err) {
      console.log(err.response);
      if (err.response.status === 409) {
        toast.error(err.response.data.message);
      }
    }
  }
);
export const updatePatientAsync = createAsyncThunk(
  "patients/updatePatientAsync",
  async (payload) => {
    try {
      const response = await baseApi.put(`patients/${payload.id}`, payload);
      if(response.status=="200"){
        toast.success("Updated Successfully!");  
        return payload;
      }
    } catch (err) {
      toast.error(friendlyError(err, "Unable to update this case. Please try again."));
      throw err;
    }
  }
); 

export const deletePatientAsync = createAsyncThunk(
  "patients/deletePatientAsync",
  async (payload) => {
    await baseApi.delete(`patients/${payload.id}`);

    return payload;
  }
);

const initialState = {
  patients: [],
  patientsTotal: 0,
  drafts: [],
  patient: [],
  adminStats: null,
  errorMessage: "",
};

const resetPatientData = (state) => {
  state.patients = [];
  state.patientsTotal = 0;
  state.drafts = [];
  state.patient = [];
  state.adminStats = null;
  state.errorMessage = "";
};

const patientSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {},
  extraReducers: {
    [postLoginAsync.fulfilled]: (state) => {
      resetPatientData(state);
    },
    [logout.fulfilled]: (state) => {
      resetPatientData(state);
    },

    [getAdminStatsAsync.fulfilled]: (state, action) => {
      state.adminStats = action.payload;
    },

    [getPatientsAsync.fulfilled]: (state, action) => {
      state.patients = action.payload.data;
      state.patientsTotal = action.payload.total;
    },

    [getAllCasesAsync.fulfilled]: (state, action) => {
      return { ...state, patients: action.payload };
    },

    [getPatientsByIdAsync.fulfilled]: (state, action) => {
      console.log("get data by Id");
      return { ...state, patient: action.payload };
    },
    [getPatientsDraftsAsync.fulfilled]: (state, action) => {
      console.log("get Drafts data by Id");
      return { ...state, drafts: action.payload };
    },

    [getPatientsByIdAsync.rejected]: (state) => {
      console.log("Error occured Retrieving Data!");
      return { ...state, errorMessage: "Something went wrong" };
    },

    [getFamilyMembersAsync.fulfilled]: (state, action) => {
      console.log("Get Family Members Data");
      return { ...state, patients: action.payload };
    },

    [addPatientAsync.fulfilled]: (state, action) => {
      state.patients.push(action.payload);
      return { ...state, patients: action.payload };
    },
    [addFamilyAsync.fulfilled]: (state, action) => {
      state.patients.push(action.payload);
      return { ...state, patients: action.payload };
    },

    [updatePatientAsync.fulfilled]: (state, action) => {
      const index = state.patients.findIndex(
        (patient) => patient.id === action.payload.id
      );

      if (index !== -1) {
        state.patients[index] = action.payload;
      }

      if (
        state.patient &&
        !Array.isArray(state.patient) &&
        state.patient.id === action.payload.id
      ) {
        state.patient = { ...state.patient, ...action.payload };
      }
    },
    [updatePatientAsync.rejected]: (state) => {
      console.log("Error occured");
      return { ...state, errorMessage: "Something went wrong" };
    },
    [deletePatientAsync.fulfilled]: (state, action) => {
      let patients = state.patients.filter(
        (patient) => patient.id !== action.payload.id
      );

      return { ...state, patients: patients };
    },
  },
});

export default patientSlice.reducer;
